<template>
  <div>
    <b-row>
      <b-col class="text-left">
        <h1>{{ $i18n.t('authLog.title') }}</h1>
      </b-col>
    </b-row>
    <b-row class="align-items-center">
      <b-col cols="5">
        <b-form-datepicker
          class="mb-2"
          v-model="filters.fromDate"
          reset-button
          locale="fr"
          :placeholder="$i18n.t('common.startDate')"
          :label-reset-button="$i18n.t('common.reset')"
          :label-no-date-selected="$i18n.t('common.startDate')"
        >
        </b-form-datepicker>
      </b-col>
      <b-col cols="5">
        <b-form-datepicker
          class="mb-2"
          v-model="filters.toDate"
          reset-button
          locale="fr"
          :placeholder="$i18n.t('common.endDate')"
          :label-reset-button="$i18n.t('common.reset')"
          :label-no-date-selected="$i18n.t('common.endDate')"
        >
        </b-form-datepicker>
      </b-col>
      <b-col cols="2" class="text-right">
        <b-overlay
          :show="isLoadingDownloadCSV"
          spinner-variant="primary"
        >
          <b-button
            class="w-100"
            variant="outline-primary"
            :disabled="isLoading"
            @click="downloadCSV"
            ><i class="fas fa-download"></i>&nbsp;{{
              $i18n.t('authLog.csvExport')
            }}
          </b-button>
        </b-overlay>
      </b-col>
    </b-row>
    <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
      <CustomTable
        class="p-0 text-left"
        :items="authLogsPage"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        @sort-changed="onSortChanged"
        @page-changed="onPageChanged"
        @per-page-changed="onPerPageChanged"
      >
        <template #cell(eventDate)="data">
          {{ data.item.eventDate.toLocaleString('fr') }}
        </template>
        <template #empty>
          <h4 class="text-secondary text-center mt-1">
            {{ $i18n.t('authLog.notFound') }}
          </h4>
        </template>
      </CustomTable>
    </b-overlay>
  </div>
</template>

<script>
import CustomTable from '@/components/CustomTable';
import moment from 'moment';
import { exportToCSV } from '@/helpers/csvHelper';

export default {
  name: 'AuthLogs',
  components: {
    CustomTable,
  },
  data() {
    return {
      isLoadingDownloadCSV: false,
      isLoading: true,
      perPage: 10,
      totalRows: null,
      currentPage: 1,
      sort: null,
      filters: { fromDate: null, toDate: null },
      fields: [
        {
          key: 'userId',
          label: this.$i18n.t('authLog.fields.userId'),
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'username',
          label: this.$i18n.t('authLog.fields.username'),
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'terminalId',
          label: this.$i18n.t('authLog.fields.terminalId'),
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'eventType',
          label: this.$i18n.t('authLog.fields.eventType'),
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'eventDate',
          label: this.$i18n.t('authLog.fields.eventDate'),
          sortable: true,
          thClass: 'bg-success text-light',
        },
      ],
      authLogs: [],
      authLogsBase: [],
    };
  },
  async mounted() {
    let response = await this.$kuzzle.document.search(
      'logs',
      'auth',
      {},
      { size: 500 }
    );

    let results = [];
    while (response) {
      results.push(...response._result.hits);
      response = await response.next();
    }

    this.authLogsBase = this.formatAuthLogs(results);
    this.authLogs = this.authLogsBase;
    // Sort by desc date
    this.onSortChanged({ sortBy: 'eventDate', sortDesc: true });

    this.totalRows = this.authLogsBase.length;
    this.onPageChanged(1);
    this.isLoading = false;
  },
  computed: {
    authLogsPage() {
      const startPage = (this.currentPage - 1) * this.perPage;
      const endPage = this.currentPage * this.perPage;
      return this.authLogs.slice(startPage, endPage);
    },
  },
  methods: {
    formatAuthLogs(results) {
      const formattedResults = results.map((result) => {
        const eventDate = new Date(result._source._kuzzle_info.createdAt);
        return { ...result._source, eventDate };
      });

      return formattedResults;
    },
    downloadCSV() {
      this.isLoadingDownloadCSV = true;

      const now = new Date(Date.now());
      const formattedDate = `${now.getFullYear()}_${
        now.getMonth() + 1
      }_${now.getDate()}-${now.getHours()}_${now.getMinutes()}`;
      const filename = `auth-logs_${formattedDate}.csv`;

      const headersKey = [];
      const headersLabel = [];
      for (const field of this.fields) {
        headersKey.push(field.key);
        headersLabel.push(field.label);
      }

      const newAuthLogs = this.authLogs.map((AuthLog) => {
        const filteredObj = {};

        headersKey.forEach((key) => {
          if (AuthLog.hasOwnProperty(key)) {
            const value = AuthLog[key];
            const formattedValue =
              value instanceof Date ? value.toLocaleString('fr') : value;
            filteredObj[key] = formattedValue;
          } else {
            filteredObj[key] = null;
          }
        });

        return filteredObj;
      });

      exportToCSV(filename, newAuthLogs, headersLabel);
      this.isLoadingDownloadCSV = false;
    },
    onSortChanged(value) {
      // Don't re-fetch data because it can be heavy
      this.authLogs = this.authLogs.sort((a, b) => {
        const inverseMultiplier = value.sortDesc ? 1 : -1;
        a = a[value.sortBy];
        b = b[value.sortBy];

        if (a > b || !a) return inverseMultiplier * -1;
        else if (a < b || !b) return inverseMultiplier * 1;

        return 0;
      });
      this.sort = value;
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
    onPerPageChanged(perPage) {
      this.perPage = perPage;
    },
    filterAuthLogs(fromDate, toDate) {
      this.authLogs = this.authLogsBase;
      if (fromDate != null) {
        this.authLogs =
          this.authLogs.filter((item) => item.eventDate >= fromDate) || [];
      }
      if (toDate != null) {
        this.authLogs =
          this.authLogs.filter((item) => item.eventDate < toDate) || [];
      }

      // Apply same sort
      this.onSortChanged(this.sort);
    },
  },
  watch: {
    // The computed equivalent is not working => not updating value correctly
    'authLogs.length'(length) {
      this.totalRows = length;
    },
    filters: {
      handler(filters) {
        let fromDate = moment(filters.fromDate);
        if (!fromDate._isValid) fromDate = null;
        // Reach the end of day
        let toDate = moment(filters.toDate).add(1, 'days');
        if (!toDate._isValid) toDate = null;

        this.filterAuthLogs(fromDate, toDate);
      },
      deep: true,
    },
  },
};
</script>
